// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { Entitlement } from '@api/constants';
import { EamcPath, SettingsPath } from '@portal/constants';
import {
  FileSpreadsheet,
  LayoutGrid,
  type LucideIcon,
  Paintbrush,
  ShieldCheck,
  UserRound,
  Waypoints,
} from 'lucide-react';
import {
  adminGate,
  EAMC_ACCESS,
  type ItemGate,
  type ItemTextKey,
  REPORT_ITEMS,
  type ReportItem,
  type ReportKey,
} from './SettingsRegistry';

export const SETTINGS_TAB_KEYS = {
  overview: 'overview',
  users: 'users',
  identityUsers: 'identity-users',
  applications: 'applications',
  samlIdp: 'saml-idp',
  eamIntegration: 'eam-integration',
  vpamIntegration: 'vpam-integration',
  aipIntegration: 'aip-integration',
  idvConnections: 'oidc-integrations',
  security: 'security',
  passwordPolicy: 'password-policy',
  brandingAndLabels: 'branding-and-labels',
  biometricConsent: 'biometric-consent',
  hubReports: 'hub-reports',
  endpoints: 'endpoints',
  workflows: 'workflows',
  reports: 'reports',
} as const;

export type SettingsTab = (typeof SETTINGS_TAB_KEYS)[keyof typeof SETTINGS_TAB_KEYS];

const TRANSLATION_ROOT = 'portal.settings.tabs-title';

type ItemRoutes =
  | { owner: 'global'; routes: { global: SettingsPath } }
  | { owner: 'eamc'; routes: { eamc: EamcPath } }
  | { owner: 'both'; routes: { global: SettingsPath; eamc: EamcPath } };

export type PageItem = ItemRoutes & {
  key: SettingsTab;
  gate: ItemGate;
  titleKey: ItemTextKey;
  hideTitle?: true;
  navLabelKey?: ItemTextKey;
  navGroup?: 'users';
};

const globalOnly = (path: SettingsPath) => ({ owner: 'global', routes: { global: path } }) as const;

const eamcOnly = (path: EamcPath) => ({ owner: 'eamc', routes: { eamc: path } }) as const;

const shared = (settings: SettingsPath, eamc: EamcPath) =>
  ({ owner: 'both', routes: { global: settings, eamc } }) as const;

export const SETTINGS_ITEMS: readonly PageItem[] = [
  {
    key: SETTINGS_TAB_KEYS.users,
    ...shared(SettingsPath.ENTRA_ID_USERS, EamcPath.ENTRA_ID_USERS),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.entra-id-users`,
    navGroup: 'users',
  },
  {
    key: SETTINGS_TAB_KEYS.identityUsers,
    ...shared(SettingsPath.IDENTITY_USERS, EamcPath.IDENTITY_USERS),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.identity-users`,
    navGroup: 'users',
  },
  {
    key: SETTINGS_TAB_KEYS.applications,
    ...eamcOnly(EamcPath.APPLICATIONS),
    gate: adminGate([Entitlement.SSO]),
    titleKey: `${TRANSLATION_ROOT}.applications`,
  },
  {
    key: SETTINGS_TAB_KEYS.endpoints,
    ...eamcOnly(EamcPath.ENDPOINTS),
    gate: adminGate([Entitlement.SSO], ['ENABLE_ENDPOINTS']),
    titleKey: `${TRANSLATION_ROOT}.endpoints`,
  },
  {
    key: SETTINGS_TAB_KEYS.workflows,
    ...eamcOnly(EamcPath.WORKFLOWS),
    gate: EAMC_ACCESS,
    titleKey: 'portal.eamc.security.title',
  },
  {
    key: SETTINGS_TAB_KEYS.reports,
    ...eamcOnly(EamcPath.REPORTS),
    gate: EAMC_ACCESS,
    titleKey: `${TRANSLATION_ROOT}.reports`,
  },
  {
    key: SETTINGS_TAB_KEYS.overview,
    ...globalOnly(SettingsPath.OVERVIEW),
    gate: adminGate(),
    titleKey: 'portal.settings.nav.overview',
    hideTitle: true,
  },
  {
    key: SETTINGS_TAB_KEYS.samlIdp,
    ...globalOnly(SettingsPath.SAML_IDP),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.saml-idp`,
  },
  {
    key: SETTINGS_TAB_KEYS.eamIntegration,
    ...globalOnly(SettingsPath.EAM_INTEGRATION),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.eam-integration`,
  },
  {
    key: SETTINGS_TAB_KEYS.vpamIntegration,
    ...globalOnly(SettingsPath.VPAM_INTEGRATION),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.vpam-integration`,
  },
  {
    key: SETTINGS_TAB_KEYS.aipIntegration,
    ...globalOnly(SettingsPath.AIP_INTEGRATION),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.aip-integration`,
  },
  {
    key: SETTINGS_TAB_KEYS.idvConnections,
    ...globalOnly(SettingsPath.IDV_CONNECTIONS),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.idv-connections`,
  },
  {
    key: SETTINGS_TAB_KEYS.security,
    ...globalOnly(SettingsPath.SECURITY),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.hub-access`,
  },
  {
    key: SETTINGS_TAB_KEYS.passwordPolicy,
    ...globalOnly(SettingsPath.PASSWORD_POLICY),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.authentication-options`,
  },
  {
    key: SETTINGS_TAB_KEYS.brandingAndLabels,
    ...globalOnly(SettingsPath.BRANDING_AND_LABELS),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.branding-and-labels`,
  },
  {
    key: SETTINGS_TAB_KEYS.biometricConsent,
    ...globalOnly(SettingsPath.BIOMETRIC_CONSENT),
    gate: adminGate(),
    titleKey: 'portal.appearance.org-consent-title',
  },
  {
    key: SETTINGS_TAB_KEYS.hubReports,
    ...globalOnly(SettingsPath.HUB_REPORTS),
    gate: adminGate(),
    titleKey: `${TRANSLATION_ROOT}.hub-reports`,
  },
];

export type GlobalPage = Extract<PageItem, { owner: 'global' | 'both' }>;

type NavGroupKey =
  | 'overview'
  | 'integrations'
  | 'hub-security'
  | 'customization'
  | 'users'
  | 'hub-reports';

type NavEntry<Page> = { page: Page; labelKey: ItemTextKey; overviewLabelKey: ItemTextKey };

type OverviewLink<Report> = {
  kind: 'preselect' | 'download';
  report: Report;
  labelKey: ItemTextKey;
};

type NavGroupOf<Page, Report> = { key: NavGroupKey; icon: LucideIcon; labelKey: ItemTextKey } & (
  | { kind: 'page'; page: Page; links: readonly OverviewLink<Report>[] }
  | { kind: 'group'; entries: readonly NavEntry<Page>[] }
);

export type VisibleGroup = NavGroupOf<GlobalPage, ReportItem>;

const NAV_ROOT = 'portal.settings.nav';
const OVERVIEW_ROOT = 'portal.settings.overview';

const entry = (
  page: SettingsTab,
  labelKey: ItemTextKey,
  overviewLabelKey: ItemTextKey = labelKey,
) => ({ page, labelKey, overviewLabelKey });

const GLOBAL_NAV: readonly NavGroupOf<SettingsTab, ReportKey>[] = [
  {
    key: 'overview',
    icon: LayoutGrid,
    labelKey: `${NAV_ROOT}.overview`,
    kind: 'page',
    page: SETTINGS_TAB_KEYS.overview,
    links: [],
  },
  {
    key: 'integrations',
    icon: Waypoints,
    labelKey: `${NAV_ROOT}.integrations`,
    kind: 'group',
    entries: [
      entry(SETTINGS_TAB_KEYS.eamIntegration, `${NAV_ROOT}.eam`),
      entry(
        SETTINGS_TAB_KEYS.samlIdp,
        `${TRANSLATION_ROOT}.saml-idp`,
        `${OVERVIEW_ROOT}.saml-idps`,
      ),
      entry(SETTINGS_TAB_KEYS.idvConnections, `${NAV_ROOT}.identity-verification`),
    ],
  },
  {
    key: 'hub-security',
    icon: ShieldCheck,
    labelKey: `${NAV_ROOT}.hub-security`,
    kind: 'group',
    entries: [
      entry(SETTINGS_TAB_KEYS.security, `${TRANSLATION_ROOT}.hub-access`),
      entry(SETTINGS_TAB_KEYS.passwordPolicy, `${TRANSLATION_ROOT}.authentication-options`),
    ],
  },
  {
    key: 'customization',
    icon: Paintbrush,
    labelKey: `${NAV_ROOT}.customization`,
    kind: 'group',
    entries: [
      entry(SETTINGS_TAB_KEYS.brandingAndLabels, `${NAV_ROOT}.branding-and-labels`),
      entry(
        SETTINGS_TAB_KEYS.biometricConsent,
        `${NAV_ROOT}.biometric-consent`,
        `${OVERVIEW_ROOT}.biometric-consent`,
      ),
    ],
  },
  {
    key: 'users',
    icon: UserRound,
    labelKey: `${NAV_ROOT}.users`,
    kind: 'group',
    entries: [
      entry(
        SETTINGS_TAB_KEYS.identityUsers,
        `${NAV_ROOT}.identity-directory`,
        `${TRANSLATION_ROOT}.identity-users`,
      ),
      entry(SETTINGS_TAB_KEYS.users, `${NAV_ROOT}.entra-id`, `${TRANSLATION_ROOT}.entra-id-users`),
    ],
  },
  {
    key: 'hub-reports',
    icon: FileSpreadsheet,
    labelKey: `${NAV_ROOT}.hub-reports`,
    kind: 'page',
    page: SETTINGS_TAB_KEYS.hubReports,
    links: [
      {
        kind: 'preselect',
        report: 'login-activity',
        labelKey: `${TRANSLATION_ROOT}.login-activity-report`,
      },
      { kind: 'download', report: 'user-data', labelKey: `${OVERVIEW_ROOT}.user-data` },
      {
        kind: 'preselect',
        report: 'enrollment-activity',
        labelKey: `${TRANSLATION_ROOT}.enrollment-activity-report`,
      },
    ],
  },
];

export const isGlobalPage = (item: PageItem): item is GlobalPage => item.owner !== 'eamc';

const resolve = <T extends { key: string }>(items: readonly T[], key: T['key']): T => {
  const item = items.find(candidate => candidate.key === key);
  if (!item) {
    throw new Error(`Global nav references "${key}", which is not a /settings item`);
  }
  return item;
};

const resolvePage = (key: SettingsTab): GlobalPage =>
  resolve(SETTINGS_ITEMS.filter(isGlobalPage), key);

const resolveReport = (key: ReportKey): ReportItem =>
  resolve(
    REPORT_ITEMS.filter(report => report.owner !== 'eamc'),
    key,
  );

export const OVERVIEW_PAGE = resolvePage(SETTINGS_TAB_KEYS.overview);

export const GLOBAL_PAGES = SETTINGS_ITEMS.filter(isGlobalPage).filter(
  page => page !== OVERVIEW_PAGE,
);

const RESOLVED_NAV: readonly VisibleGroup[] = GLOBAL_NAV.map(group =>
  group.kind === 'page'
    ? {
        ...group,
        page: resolvePage(group.page),
        links: group.links.map(link => ({ ...link, report: resolveReport(link.report) })),
      }
    : { ...group, entries: group.entries.map(item => ({ ...item, page: resolvePage(item.page) })) },
);

export const visibleNav = (available: (gate: ItemGate) => boolean): readonly VisibleGroup[] =>
  RESOLVED_NAV.flatMap((group): VisibleGroup[] => {
    if (group.kind === 'page') {
      return available(group.page.gate)
        ? [{ ...group, links: group.links.filter(link => available(link.report.gate)) }]
        : [];
    }
    const entries = group.entries.filter(item => available(item.page.gate));
    return entries.length > 0 ? [{ ...group, entries }] : [];
  });
